import React, { useState, useEffect, useRef } from 'react';
import './ResumeSubPage.css';
import { evaluateResume, exportJdEvaluationToPdf, MAX_FILE_SIZE_MB } from '../../services/resumeService';
import { listCandidateResumeOptions, fetchCandidateResumeAsFile } from '../../services/adminCandidateResumeService';
import EvaluationUsageView from '../../components/Resume/EvaluationUsageView';

const COACH_EVALUATE_TYPE = 'resume-screening-fulltime-evaluate-coach';
const ACCEPT = '.pdf,.docx';
const MAX_BYTES = 1024 * 1024; // 1MB (matches existing flow)

/** Split coach markdown on `# Section` lines (prompt OUTPUT FORMAT). */
function parseCoachReportSections(text) {
  const normalized = (text || '').replace(/\r\n/g, '\n').trimEnd();
  if (!normalized.trim()) return [];

  const lines = normalized.split('\n');
  const headerIndices = [];
  for (let i = 0; i < lines.length; i += 1) {
    const m = lines[i].match(/^#\s+(.+)$/);
    if (m) headerIndices.push({ i, title: m[1].trim() });
  }

  if (headerIndices.length === 0) {
    return [{ title: null, body: normalized.trim() }];
  }

  const sections = [];
  const firstLine = headerIndices[0].i;
  const preamble = lines.slice(0, firstLine).join('\n').trim();
  if (preamble) {
    sections.push({ title: null, body: preamble });
  }

  headerIndices.forEach((h, idx) => {
    const start = h.i + 1;
    const end = idx + 1 < headerIndices.length ? headerIndices[idx + 1].i : lines.length;
    sections.push({ title: h.title, body: lines.slice(start, end).join('\n').trim() });
  });

  return sections;
}

function CoachReportView({ text }) {
  const sections = parseCoachReportSections(text);
  return (
    <div className="jd-eval coach-report" role="region" aria-label="Coach report">
      {sections.map((sec, idx) => {
        const isPreamble = !sec.title;
        const cardClass = ['jd-eval__section', 'jd-eval__section--card', isPreamble ? 'coach-report__card--preamble' : '']
          .filter(Boolean)
          .join(' ');
        return (
          <section key={idx} className={cardClass}>
            {sec.title ? <h4 className="coach-report__section-title">{sec.title}</h4> : null}
            <div className="coach-report__body">{sec.body || '\u00a0'}</div>
          </section>
        );
      })}
    </div>
  );
}

const validateResumeFile = (f) => {
  if (!f) return null;
  const validTypes = [
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ];
  const validExt = /\.(pdf|docx)$/i.test(f.name);
  if (!validExt && !validTypes.includes(f.type)) {
    return 'Please load a PDF or DOCX resume.';
  }
  if (f.size > MAX_BYTES) return `File must be under ${MAX_FILE_SIZE_MB}MB.`;
  return null;
};

const EvaluateFulltimeCoachResumeAdmin = () => {
  const [candidateOptions, setCandidateOptions] = useState([]);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [selectedCandidateId, setSelectedCandidateId] = useState('');
  const [loadingResume, setLoadingResume] = useState(false);
  const [file, setFile] = useState(null);

  const [resumeMode, setResumeMode] = useState('select'); // select | upload
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef(null);

  const [jdText, setJdText] = useState('');
  const [loading, setLoading] = useState(false);
  const [evaluation, setEvaluation] = useState(null);
  const [evaluationUsage, setEvaluationUsage] = useState(null);
  const [error, setError] = useState(null);
  const [pdfExportLoading, setPdfExportLoading] = useState(false);

  const clearResult = () => {
    setEvaluation(null);
    setEvaluationUsage(null);
    setError(null);
    setPdfExportLoading(false);
  };

  const handleLoadedFile = (f) => {
    const err = validateResumeFile(f);
    if (err) {
      setError(err);
      setFile(null);
      return false;
    }
    setError(null);
    setFile(f);
    return true;
  };

  const loadCandidates = async () => {
    setLoadingCandidates(true);
    try {
      const options = await listCandidateResumeOptions();
      setCandidateOptions(options);
    } catch (e) {
      setError(e.message || 'Failed to load candidate list.');
    } finally {
      setLoadingCandidates(false);
    }
  };

  useEffect(() => {
    loadCandidates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChangeCandidate = (e) => {
    const v = e.target.value;
    setSelectedCandidateId(v);
    setFile(null);
    clearResult();
  };

  const onLoadCandidateResume = async () => {
    if (!selectedCandidateId) return;
    setLoadingResume(true);
    setError(null);
    clearResult();
    try {
      const f = await fetchCandidateResumeAsFile(selectedCandidateId);
      const err = validateResumeFile(f);
      if (err) {
        setError(err);
        setFile(null);
        return;
      }
      setFile(f);
    } catch (e) {
      setError(e.message || 'Failed to load candidate resume.');
      setFile(null);
    } finally {
      setLoadingResume(false);
    }
  };

  const onPickUploadResume = () => {
    setResumeMode('upload');
    clearResult();
    setFile(null);
    setError(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  const onBackToCandidateSelection = () => {
    setResumeMode('select');
    clearResult();
    setFile(null);
    setError(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const f = e.dataTransfer.files[0];
    if (f) {
      setResumeMode('upload');
      clearResult();
      handleLoadedFile(f);
    }
  };

  const onDragOver = (e) => {
    e.preventDefault();
    setDragActive(true);
  };

  const onDragLeave = () => {
    setDragActive(false);
  };

  const onUploadClick = () => {
    inputRef.current?.click();
  };

  const onInputChange = (e) => {
    const f = e.target.files[0];
    if (f) {
      setResumeMode('upload');
      clearResult();
      handleLoadedFile(f);
    }
    e.target.value = '';
  };

  const onClearFile = (e) => {
    e.stopPropagation();
    if (loading || loadingResume) return;
    setFile(null);
    setError(null);
    setEvaluation(null);
    setEvaluationUsage(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  const onSubmit = async () => {
    if (!file) {
      setError('Please load a candidate resume first.');
      return;
    }
    setLoading(true);
    setError(null);
    setEvaluation(null);
    setEvaluationUsage(null);
    try {
      const jd = jdText.trim() ? jdText.trim() : null;
      const result = await evaluateResume(file, COACH_EVALUATE_TYPE, jd);
      if (result.success) {
        setEvaluation(result.evaluation);
        setEvaluationUsage(result.usage || null);
      } else {
        setError(result.error || 'Evaluation failed.');
      }
    } catch (e) {
      setError(e.message || 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  };

  const onExportPdf = async () => {
    if (!evaluation || pdfExportLoading) return;
    setPdfExportLoading(true);
    try {
      const { blob, filename } = await exportJdEvaluationToPdf(evaluation.trim());
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message || 'PDF export failed.');
    } finally {
      setPdfExportLoading(false);
    }
  };

  return (
    <div className="resume-subpage">
      <h2>Full-time Coach</h2>

      {resumeMode === 'select' ? (
        <div style={{ marginBottom: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
            <label style={{ fontWeight: 700, color: '#111827', fontSize: '0.875rem' }}>Select candidate resume</label>
            <select
              value={selectedCandidateId}
              onChange={onChangeCandidate}
              disabled={loadingCandidates || loadingResume || loading}
              style={{
                flex: '1 1 22rem',
                padding: '0.55rem 0.75rem',
                borderRadius: 8,
                border: '1px solid #d1d5db',
                background: '#fff',
              }}
              aria-label="Select candidate"
            >
              <option value="">{loadingCandidates ? 'Loading...' : 'Choose a candidate...'}</option>
              {candidateOptions.map((opt) => (
                <option key={opt.userId} value={opt.userId}>
                  {opt.displayName}
                </option>
              ))}
            </select>
            {selectedCandidateId ? (
              <button
                type="button"
                className="resume-subpage__submit"
                onClick={onLoadCandidateResume}
                disabled={loadingResume || loading}
                style={{ whiteSpace: 'nowrap' }}
              >
                {loadingResume ? 'Loading...' : 'Load'}
              </button>
            ) : null}
          </div>

          <div style={{ marginTop: '0.75rem' }}>
            <button
              type="button"
              className="resume-subpage__submit"
              onClick={onPickUploadResume}
              disabled={loading || loadingResume}
            >
              Upload a resume file
            </button>
          </div>
        </div>
      ) : null}

      {resumeMode === 'upload' ? (
        <div
          className={`resume-upload-zone ${dragActive ? 'resume-upload-zone--drag' : ''} ${error ? 'resume-upload-zone--error' : ''}`}
          onDrop={onDrop}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onClick={onUploadClick}
        >
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            onChange={onInputChange}
            className="resume-upload-zone__input"
            disabled={loading}
          />
          {file ? (
            <div className="resume-upload-zone__file">
              <span className="resume-upload-zone__file-name">{file.name}</span>
              <span className="resume-upload-zone__file-size">({(file.size / 1024).toFixed(1)} KB)</span>
              <button
                type="button"
                className="resume-upload-zone__clear"
                onClick={onClearFile}
                disabled={loading}
                aria-label="Remove file"
                title="Remove and re-upload"
              >
                ×
              </button>
            </div>
          ) : (
            <p className="resume-upload-zone__hint">Drop your resume here, or click to browse. Max {MAX_FILE_SIZE_MB}MB · PDF or DOCX</p>
          )}
        </div>
      ) : null}

      {resumeMode === 'upload' ? (
        <div style={{ marginTop: '0.75rem' }}>
          <button type="button" className="resume-subpage__submit" onClick={onBackToCandidateSelection} disabled={loading || loadingResume}>
            Back to candidate selection
          </button>
        </div>
      ) : null}

      {file && resumeMode === 'select' ? (
        <div style={{ margin: '0 0 1rem 0', color: '#374151', fontSize: '0.9rem' }}>
          Loaded: <strong>{file.name}</strong>
        </div>
      ) : null}

      {error && <div className="resume-subpage__error">{error}</div>}

      <div className="resume-subpage__jd-container">
        <label className="resume-subpage__jd-label" htmlFor="coach-jd-textarea">
          Job description (JD) <span style={{ fontWeight: 500, color: '#6b7280' }}>(optional)</span>
        </label>
        <textarea
          id="coach-jd-textarea"
          className="resume-subpage__jd-textarea"
          placeholder="Paste the job description here (optional)..."
          value={jdText}
          onChange={(e) => {
            setJdText(e.target.value);
            if (evaluation) clearResult();
          }}
          rows={7}
          disabled={loading}
        />
      </div>

      <div className="resume-subpage__actions">
        <button type="button" className="resume-subpage__submit" onClick={onSubmit} disabled={!file || loading}>
          {loading ? 'Evaluating…' : 'Coach resume'}
        </button>
        <button
          type="button"
          className="resume-subpage__submit"
          onClick={onExportPdf}
          disabled={!evaluation || loading || pdfExportLoading}
        >
          {pdfExportLoading ? 'Preparing PDF…' : 'Download Report'}
        </button>
      </div>

      {evaluation && (
        <div className="resume-subpage__result">
          <h3>Coach report</h3>
          <CoachReportView text={evaluation} />
          <EvaluationUsageView usage={evaluationUsage} />
        </div>
      )}
    </div>
  );
};

export default EvaluateFulltimeCoachResumeAdmin;

